import _ from 'lodash';

import { mathCuttingFunction } from '@common/helpers/filters';
import { broadcast, broadcastChannels } from '../broadcast';
import { getRoundingMethod } from './roundingMethod';
import { swarmRequest } from '../util/requestMiddleware';

// --- Module state ---

let roundMethod = _.noop;
let ladder = null;
let ladderKeys = null;

// --- Ladder loading ---

function loadLadderFromSwarm(response) {
  ladder = {};
  if (Array.isArray(response?.details)) {
    response.details.forEach((item) => {
      ladder[item.Price] = `${item.Numerator}/${item.Denominator}`;
    });
  }
  ladderKeys = Object.keys(ladder).sort((a, b) => parseFloat(a) - parseFloat(b));
}

async function onSocketStateChange(e) {
  if (e.detail?.connected) {
    const response = await swarmRequest({}, 'get_permissible_odds');
    if (response?.details) {
      loadLadderFromSwarm(response);
      broadcast.removeEventListener(broadcastChannels.SocketStateChange, onSocketStateChange);
    }
  }
}

broadcast.addEventListener(broadcastChannels.SocketStateChange, onSocketStateChange);

// --- Ladder-based fractional helpers ---

// Iteratively finds the biggest decimal odd that is smaller than num
function findNearestLadderKey(num) {
  let start = 0;
  let end = ladderKeys.length - 1;
  let found = false;
  let result;
  while (!found) {
    const mid = start + Math.ceil((end - start) / 2);
    if (
      (mid === 0 && ladderKeys[mid] > num)
      || (mid === ladderKeys.length - 1 && ladderKeys[mid] < num)
      || (ladderKeys[mid] < num && num < ladderKeys[mid + 1])
    ) {
      found = true;
      result = ladderKeys[mid];
    } else if (ladderKeys[mid] < num) {
      start = mid;
    } else {
      end = mid - 1;
    }
  }
  return result;
}

function dec2fracFromLadder(dec) {
  if (ladder[dec]) return ladder[dec];
  const key = findNearestLadderKey(dec);
  return ladder[key];
}

// --- Algorithmic fractional conversion ---

function recCalc(decVal, Zcur, Dcur = 1, Dprev = 0) {
  const Znxt = 1 / (Zcur - parseInt(Zcur, 10));
  const Dnxt = Dcur * parseInt(Znxt, 10) + Dprev;
  const Nnxt = Math.round(decVal * Dnxt);
  return Nnxt / Dnxt === decVal
    ? `${Nnxt}/${Dnxt}`
    : recCalc(decVal, Znxt, Dnxt, Dcur);
}

// Use this casting method because of JS number bug for example "2.2 - 1 = 1.1999(9)"
function dec2frac(decVal) {
  const adjusted = decVal !== parseInt(decVal, 10)
    ? parseFloat(`${(parseInt(decVal, 10) - 1).toString()}.${String(decVal).split('.')[1]}`)
    : decVal - 1;
  return adjusted % 1 === 0 ? `${adjusted}/1` : recCalc(adjusted, adjusted);
}

// --- Format converters ---

// The first entry is the default when no valid format is found in config
const possibleFormats = ['decimal', 'fractional', 'american', 'hongkong', 'malay', 'indo'];

const converters = {
  decimal(value, config) {
    if (value === undefined || value === '') return value;
    const fValue = parseFloat(value);
    const iValue = parseInt(value, 10);
    return iValue !== fValue && value.toString().split('.')[1] && value.toString().split('.')[1].length > 2
      ? roundMethod(value, config.odd.roundDecimalCoefficients)
      : fValue.toFixed(2);
  },

  fractional(value, config, type) {
    if (config.odd.useLadderForFractionalFormat && type === 'fictional' && value !== undefined) {
      return `${Math.round(parseFloat(value - 1) * 100 || 0) / 100}/1`;
    }
    if (!value) return value;
    const fValue = parseFloat(value);
    const rValue = Math.round(fValue * 100 || 0) / 100;
    return config.odd.useLadderForFractionalFormat ? dec2fracFromLadder(fValue) : dec2frac(rValue);
  },

  american(value, rValue) {
    if (!value) return rValue;
    if (rValue >= 2) return `+${Math.round(100 * (rValue - 1))}`;
    if (rValue !== 1) return Math.round(-100 / (rValue - 1));
    return '-';
  },

  hongkong(value, config) {
    if (value === undefined || value === '') return value;
    const fValue = parseFloat(value);
    const iValue = parseInt(value, 10);
    const { roundDecimalCoefficients, oddDecimalFormat2Digits } = config.odd;
    let hValue = iValue !== fValue && value.toString().split('.')[1]?.length > 2
      ? Math.round((value - 1) * 10 ** roundDecimalCoefficients) / 10 ** roundDecimalCoefficients
      : (fValue - 1).toFixed(2);
    if (oddDecimalFormat2Digits) {
      hValue = (mathCuttingFunction(hValue * 10 * 10) / 100).toFixed(2);
    }
    return hValue;
  },

  malay(value, config) {
    const fValue = parseFloat(value);
    const { roundDecimalCoefficients } = config.odd;
    if (fValue === 2) return '1.000';
    if (fValue > 2) {
      const pow = 10 ** roundDecimalCoefficients;
      const raw = Math.round((1 / (1 - fValue)).toFixed(roundDecimalCoefficients + 1) * pow);
      return (raw / pow).toFixed(roundDecimalCoefficients);
    }
    return (fValue - 1).toFixed(roundDecimalCoefficients);
  },

  indo(value, config) {
    const fValue = parseFloat(value);
    const { roundDecimalCoefficients } = config.odd;
    if (fValue === 2) return '1.000';
    if (fValue > 2) return (fValue - 1).toFixed(roundDecimalCoefficients);
    const pow = 10 ** roundDecimalCoefficients;
    const raw = Math.round((1 / (1 - fValue)).toFixed(roundDecimalCoefficients + 1) * pow);
    return (raw / pow).toFixed(roundDecimalCoefficients);
  },
};

// --- Core convert ---

function convert(config, value, format, type) {
  const fValue = parseFloat(value);
  const rValue = value !== undefined && value !== '' ? Math.round(fValue * 100 || 0) / 100 : value;
  switch (format) {
    case 'decimal': return converters.decimal(value, config);
    case 'fractional': return converters.fractional(value, config, type);
    case 'american': return converters.american(value, rValue);
    case 'hongkong': return converters.hongkong(value, config);
    case 'malay': return converters.malay(value, config);
    case 'indo': return converters.indo(value, config);
    default: return rValue;
  }
}

// Fix: original cache key serialised config as "[object Object]",
// making it ignore config differences. Now we key on the relevant config values.
const convertMemo = _.memoize(
  convert,
  (config, value, format, type) => [
    value,
    format,
    type,
    config.odd.roundDecimalCoefficients,
    config.odd.useLadderForFractionalFormat,
    config.odd.oddDecimalFormat2Digits,
  ].join('_'),
);

// --- Exports ---

export const setOddConverterPartnerConfig = (roundMethodType) => {
  roundMethod = getRoundingMethod(roundMethodType);
};

// eslint-disable-next-line max-len
export default function OddConverter(config, value, format, type = null, displayKey, doesNeedOriginal) {
  format = format || config.odd.oddsFormat;
  if (value === null || value === undefined || isNaN(value)) return value;
  if (value === 1) return null;

  if (config.odd.specialOddFormat?.[format]) {
    if (!doesNeedOriginal) {
      const specialFormat = config.odd.specialOddFormat[format];
      format = specialFormat.displayKey[displayKey] || specialFormat.default;
    } else if (config.odd.specialOddFormat[format].originalOddType) {
      format = config.odd.specialOddFormat[format].originalOddType;
    }
  }

  // select default format if current one is invalid
  if (!possibleFormats.includes(format)) format = possibleFormats[0];

  return convertMemo(config, value, format, type);
}
